import { useCallback, useContext, useEffect, useRef } from 'react'
import { useStore } from 'zustand'

import { useToast } from '@/hooks'
import { transactionFilterSchema } from '@/lib/schemas/transactionFilterSchema'
import { useUser } from '@/modules/auth'
import { useAsyncError } from '@/modules/errors'
import { ProjectContext } from '@/modules/projects/components/ProjectStoreContext'
import { useFiltersActions } from '@/stores'
import type { Filter, FilterOperator } from '@/stores/filters/types'
import type { AgentStatus, ChatStore, PendingFilterAction } from '../stores'
import {
  loadChatIntroDismissed,
  loadSidebarCollapsed,
  persistChatIntroDismissed,
  persistSidebarCollapsed,
} from '../utils/chatPrefs'
import {
  type SessionTokenTotals,
  sumCostMicros,
  sumInputTokens,
  sumOutputTokens,
} from '../utils/sessionTotals'

export type { SessionTokenTotals }

const useChatStore = <T>(selector: (state: ChatStore) => T): T => {
  const stores = useContext(ProjectContext)
  const store = stores?.chatStore

  if (!store) {
    throw new Error('useChatStore must be used within ProjectProvider')
  }

  return useStore(store, selector)
}

export const useChatSessions = () => useChatStore((state) => state.sessions)
export const useLoadingSessions = () =>
  useChatStore((state) => state.loadingSessions)
export const useArchivedSessions = () =>
  useChatStore((state) => state.archivedSessions)
export const useLoadingArchived = () =>
  useChatStore((state) => state.loadingArchived)
export const useSessionFilter = () =>
  useChatStore((state) => state.sessionFilter)
export const useSessionSearch = () =>
  useChatStore((state) => state.sessionSearch)
export const useChatMessages = () => useChatStore((state) => state.messages)
export const useCancelledReportEditTokens = () =>
  useChatStore((state) => state.cancelledReportEditTokens)
export const useChatDraft = () =>
  useChatStore((state) =>
    state.draftAssistant &&
    state.draftAssistant.sessionId === state.currentSessionId
      ? state.draftAssistant
      : null,
  )

export const useSessionTokenTotals = (): SessionTokenTotals => {
  const inputTokens = useChatStore(sumInputTokens)
  const outputTokens = useChatStore(sumOutputTokens)
  const costMicros = useChatStore(sumCostMicros)
  return { inputTokens, outputTokens, costMicros }
}

export const useSelectedAgent = () =>
  useChatStore((state) => state.selectedAgent)
export const useCurrentSession = () =>
  useChatStore(
    (state) =>
      state.sessions.find((session) => session.id === state.currentSessionId) ??
      null,
  )
export const useDiagnosticsEnabled = (): boolean =>
  useChatStore((state) => state.diagnosticsEnabled)
export const useSidebarCollapsed = (): boolean =>
  useChatStore((state) => state.sidebarCollapsed)
export const useSidebarWidth = (): number =>
  useChatStore((state) => state.sidebarWidth)
export const useSidebarFullScreen = (): boolean =>
  useChatStore((state) => state.sidebarFullScreen)
export const useHistoryPopoverOpen = (): boolean =>
  useChatStore((state) => state.historyPopoverOpen)
export const useChatSending = () =>
  useChatStore(
    (state) =>
      state.sendingMessage &&
      state.optimisticUserSessionId === state.currentSessionId,
  )
export const useCurrentSessionId = () =>
  useChatStore((state) => state.currentSessionId)
export const useLoadingMessages = () =>
  useChatStore((state) => state.loadingMessages)
export const useOptimisticUserText = () =>
  useChatStore((state) =>
    state.optimisticUserSessionId === state.currentSessionId
      ? state.optimisticUserText
      : null,
  )
export const useChatLoaders = () => useChatStore((state) => state.loaders)
export const useChatMutations = () => useChatStore((state) => state.mutations)

// Opens the chat panel and queues a prompt to send once a session is ready.
// For callers outside the panel (e.g. an insights notice) that want to kick
// off a conversation with a specific question. The panel's
// `useApplyPendingSeed` does the actual send.
export const useSeedChatMessage = (): ((message: string) => void) => {
  const { setSidebarCollapsed, seedMessage } = useChatMutations()
  return useCallback(
    (message: string) => {
      setSidebarCollapsed(false)
      seedMessage(message)
    },
    [setSidebarCollapsed, seedMessage],
  )
}

export const useFetchChatSessions = (slug: string) => {
  const { fetchSessions } = useChatLoaders()
  const throwError = useAsyncError()
  useEffect(() => {
    fetchSessions(slug).catch(throwError)
  }, [slug, fetchSessions, throwError])
}

// Keeps the panel's collapsed/expanded state in sync with per-project local
// storage so it survives a page refresh. On first mount (and whenever the
// project changes) it hydrates from storage; every later change is written
// back. Re-hydrating on slug change is what prevents one project's expanded
// chat from carrying into the next.
export const useHydrateSidebarCollapsed = (slug: string): void => {
  const collapsed = useSidebarCollapsed()
  const { setSidebarCollapsed } = useChatMutations()
  const hydratedSlugRef = useRef<string | null>(null)

  useEffect(() => {
    if (hydratedSlugRef.current !== slug) {
      hydratedSlugRef.current = slug
      setSidebarCollapsed(loadSidebarCollapsed(slug))
      return
    }
    persistSidebarCollapsed(slug, collapsed)
  }, [slug, collapsed, setSidebarCollapsed])
}

// Drives the one-time "New! Try chat" nudge. Hydrates the per-user dismissed
// flag from storage after mount, and exposes `dismiss` (persist + hide). The
// flag is keyed by the Supabase user id so each person sees it once on their
// own account, even on a shared browser.
export const useChatIntro = (): { dismissed: boolean; dismiss: () => void } => {
  const dismissed = useChatStore((state) => state.chatIntroDismissed)
  const { setChatIntroDismissed } = useChatMutations()
  const userId = useUser()?.id ?? null
  const hydratedKeyRef = useRef<string | null>(null)

  useEffect(() => {
    const key = userId ?? 'anon'
    if (hydratedKeyRef.current === key) return
    hydratedKeyRef.current = key
    setChatIntroDismissed(loadChatIntroDismissed(userId))
  }, [userId, setChatIntroDismissed])

  const dismiss = useCallback(() => {
    persistChatIntroDismissed(userId)
    setChatIntroDismissed(true)
  }, [userId, setChatIntroDismissed])

  return { dismissed, dismiss }
}

export const usePendingFilters = (): PendingFilterAction | null =>
  useChatStore((state) => state.pendingFilters)

export const usePendingSeed = (): string | null =>
  useChatStore((state) => state.pendingSeed)

export const useThinkingSince = (): number | null =>
  useChatStore((state) => state.thinkingSince)

export const useDraftingTool = (): { name: string; chars: number } | null =>
  useChatStore((state) => state.draftingTool)

export const useAgentStatus = (): AgentStatus | null =>
  useChatStore((state) => state.agentStatus)

const ALLOWED_FIELDS = new Set(
  transactionFilterSchema.map((field) => field.field),
)

function sanitizeFilters(pending: PendingFilterAction): {
  filters: Filter[]
  clear: boolean
} {
  const filters: Filter[] = []
  for (const rule of pending.filters) {
    if (!rule || typeof rule !== 'object') continue
    if (!ALLOWED_FIELDS.has(rule.field)) continue
    filters.push({
      id: rule.id ?? crypto.randomUUID(),
      field: rule.field,
      operator: rule.operator as FilterOperator,
      value: rule.value,
      value2: rule.value2,
    })
  }
  return { filters, clear: pending.clear }
}

export const useApplyPendingFilters = (): void => {
  const pending = usePendingFilters()
  const { consumePendingFilters } = useChatMutations()
  const filterActions = useFiltersActions()

  useEffect(() => {
    if (!pending) return
    const { filters, clear } = sanitizeFilters(pending)
    if (clear && filters.length === 0) {
      filterActions.clearFilters()
    } else {
      filterActions.setFilters(filters)
    }
    consumePendingFilters()
  }, [pending, filterActions, consumePendingFilters])
}

// Sends a prompt queued from outside the chat (e.g. the "Have Daystrom
// Review" button) once the panel has an active session and isn't already
// streaming. Claims the seed atomically so a double-invoked effect (React
// StrictMode) sends it once, not twice (the second send would 409 on the
// in-progress turn and surface a spurious error).
export const useApplyPendingSeed = (slug: string): void => {
  const pending = usePendingSeed()
  const sessionId = useCurrentSessionId()
  const sending = useChatStore((state) => state.sendingMessage)
  const { sendMessage, claimPendingSeed } = useChatMutations()
  const { errorToast } = useToast()

  useEffect(() => {
    if (!pending || !sessionId || sending) return
    const seed = claimPendingSeed()
    if (!seed) return
    sendMessage(slug, seed).catch((error) => {
      errorToast('Could not start the review', error as Error)
    })
  }, [
    pending,
    sessionId,
    sending,
    slug,
    sendMessage,
    claimPendingSeed,
    errorToast,
  ])
}
